export type AccountProfile = {
  medopilot_user_id: string | null;
  first_name: string | null;
  last_name: string | null;
  contact_phone_e164: string | null;
  contact_phone_country_code: string | null;
  date_of_birth: string | null;
  birth_time: string | null;
  birth_city: string | null;
  birth_country: string | null;
  birth_facility_name: string | null;
  sex_assigned_at_birth: string | null;
  gender_identity: string | null;
  blood_group: string | null;
  residential_address: ProfileAddress | null;
  preferred_language: string | null;
  emergency_contacts: EmergencyContact[];
  primary_doctor: PrimaryDoctor | null;
  preferred_hospitals: PreferredHospitals | null;
  health_insurance: HealthInsurance | null;
  // Global MedoPilot Care Reminders delivery-policy gate -- distinct from this device's browser/OS
  // notification permission (see useNotificationPermission). Legacy accounts predating this field
  // have no stored value at all; normalizeProfile below treats that the same as `true`, matching
  // the backend's own default-on behavior for accounts that never explicitly opted out.
  care_reminders_enabled: boolean;
  // Convenience default for creating future schedules only -- never reinterprets or mutates a
  // schedule's own persisted timezone. Missing/never-saved stays `null`; the frontend may suggest
  // (but must not silently persist) the browser's detected timezone in that case.
  default_timezone: string | null;
  created_at: string | null;
  updated_at: string | null;
};

export type ProfileAddress = {
  line1: string | null;
  line2: string | null;
  city: string | null;
  region: string | null;
  postal_code: string | null;
  country_code: string | null;
};

export type EmergencyContact = {
  name: string;
  relationship: string;
  primary_mobile_number: string;
  alternate_mobile_number: string | null;
  whatsapp_number: string | null;
  address: ProfileAddress | null;
  priority: number;
};

export type PrimaryDoctor = {
  name: string | null;
  speciality: string | null;
  hospital_clinic_name: string | null;
  mobile_number: string | null;
  emergency_contact_number: string | null;
  address: ProfileAddress | null;
  email: string | null;
  preferred_for_emergency: boolean;
};

export type PreferredHospital = {
  name: string;
  emergency_number: string;
  address: ProfileAddress;
  ambulance_contact_number: string | null;
};

export type PreferredHospitals = {
  primary: PreferredHospital | null;
  alternate: PreferredHospital | null;
};

export type HealthInsurance = {
  insurance_company: string | null;
  policy_name: string | null;
  policy_number: string | null;
  insurance_company_contact_email: string | null;
  insurance_company_contact_phone: string | null;
};

export type HealthProfileExportSection =
  | "personal_details"
  | "health_insurance"
  | "allergies"
  | "known_health_conditions"
  | "vaccinations"
  | "active_medications"
  | "primary_doctor"
  | "emergency_contacts"
  | "preferred_hospitals";

export type HealthProfileExport = {
  blob: Blob;
  filename: string;
};

export type AccountProfilePatch = {
  first_name?: string | null;
  last_name?: string | null;
  contact_phone_e164?: string | null;
  contact_phone_country_code?: string | null;
  date_of_birth?: string | null;
  birth_time?: string | null;
  birth_city?: string | null;
  birth_country?: string | null;
  birth_facility_name?: string | null;
  sex_assigned_at_birth?: string | null;
  gender_identity?: string | null;
  blood_group?: string | null;
  residential_address?: ProfileAddress | null;
  preferred_language?: string | null;
  emergency_contacts?: EmergencyContact[];
  primary_doctor?: PrimaryDoctor | null;
  preferred_hospitals?: PreferredHospitals | null;
  health_insurance?: HealthInsurance | null;
  care_reminders_enabled?: boolean;
  default_timezone?: string | null;
};

type ProfileResponse = {
  profile?: Partial<AccountProfile>;
};

export class AccountServiceError extends Error {
  constructor(message = "We could not reach your MedoPilot profile right now.") {
    super(message);
    this.name = "AccountServiceError";
  }
}

// Thrown when the account has an open deletion request and is suspended from normal access. It is
// not an error to show inline -- the app renders the dedicated suspended-account experience.
export class AccountSuspendedError extends AccountServiceError {
  constructor() {
    super("This MedoPilot account is scheduled for deletion.");
    this.name = "AccountSuspendedError";
  }
}

export type AccountLifecycleStatus = "ACTIVE" | "PENDING_DELETION";

export type AccountLifecycle = {
  status: AccountLifecycleStatus;
  deletion: {
    requested_at: string | null;
    purge_after: string | null;
    grace_period_days: number | null;
  } | null;
};

// Whole days between now and purge_after, rounded up (so any part of a day counts as a day), never
// below 0. Returns null when purge_after is missing or unparseable. The backend is authoritative
// for whether a restore is still allowed (it rejects at/after purge_after) -- this is display only.
export function graceDaysRemaining(purgeAfter: string | null | undefined, now: Date = new Date()): number | null {
  if (!purgeAfter) {
    return null;
  }
  const deadline = new Date(purgeAfter).getTime();
  if (Number.isNaN(deadline)) {
    return null;
  }
  return Math.max(0, Math.ceil((deadline - now.getTime()) / 86_400_000));
}

async function errorCode(response: Response): Promise<string | null> {
  try {
    const body = (await response.clone().json()) as { error?: { code?: unknown } };
    return typeof body?.error?.code === "string" ? body.error.code : null;
  } catch {
    return null;
  }
}

function normalizeLifecycle(value: unknown): AccountLifecycle {
  const account = (value as { account?: Record<string, unknown> } | null)?.account ?? {};
  const status: AccountLifecycleStatus = account.status === "PENDING_DELETION" ? "PENDING_DELETION" : "ACTIVE";
  const deletion = account.deletion && typeof account.deletion === "object" ? (account.deletion as Record<string, unknown>) : null;
  if (status !== "PENDING_DELETION" || !deletion) {
    return { status: "ACTIVE", deletion: null };
  }
  return {
    status,
    deletion: {
      requested_at: typeof deletion.requested_at === "string" ? deletion.requested_at : null,
      purge_after: typeof deletion.purge_after === "string" ? deletion.purge_after : null,
      grace_period_days: typeof deletion.grace_period_days === "number" ? deletion.grace_period_days : null,
    },
  };
}

async function requestAccountLifecycle(
  apiBaseUrl: string,
  accessToken: string | undefined,
  method: "GET" | "POST" | "DELETE",
  signal?: AbortSignal,
): Promise<AccountLifecycle> {
  if (!accessToken) {
    throw new AccountServiceError("Please sign in again to manage your account.");
  }

  const path = method === "GET" ? "/v1/me/account" : "/v1/me/account/deletion";
  const response = await fetch(`${apiBaseUrl}${path}`, {
    method,
    signal,
    headers: { Accept: "application/json", Authorization: `Bearer ${accessToken}` },
  });

  if (!response.ok) {
    if (response.status === 409) {
      throw new AccountServiceError(
        "This account has already entered permanent deletion and can no longer be restored.",
      );
    }
    if (response.status === 401 || response.status === 403) {
      throw new AccountServiceError("Please sign in again to manage your account.");
    }
    throw new AccountServiceError("We could not update your account right now. Please try again.");
  }

  return normalizeLifecycle(await response.json());
}

function normalizeProfile(profile: Partial<AccountProfile> | undefined): AccountProfile {
  return {
    medopilot_user_id: typeof profile?.medopilot_user_id === "string" ? profile.medopilot_user_id : null,
    first_name: typeof profile?.first_name === "string" ? profile.first_name : null,
    last_name: typeof profile?.last_name === "string" ? profile.last_name : null,
    contact_phone_e164: typeof profile?.contact_phone_e164 === "string" ? profile.contact_phone_e164 : null,
    contact_phone_country_code:
      typeof profile?.contact_phone_country_code === "string" ? profile.contact_phone_country_code.toUpperCase() : null,
    date_of_birth: typeof profile?.date_of_birth === "string" ? profile.date_of_birth : null,
    birth_time: typeof profile?.birth_time === "string" ? profile.birth_time : null,
    birth_city: typeof profile?.birth_city === "string" ? profile.birth_city : null,
    birth_country: typeof profile?.birth_country === "string" ? profile.birth_country.toUpperCase() : null,
    birth_facility_name: typeof profile?.birth_facility_name === "string" ? profile.birth_facility_name : null,
    sex_assigned_at_birth: typeof profile?.sex_assigned_at_birth === "string" ? profile.sex_assigned_at_birth : null,
    gender_identity: typeof profile?.gender_identity === "string" ? profile.gender_identity : null,
    blood_group: typeof profile?.blood_group === "string" ? profile.blood_group.toUpperCase() : null,
    residential_address: normalizeAddress(profile?.residential_address),
    preferred_language: typeof profile?.preferred_language === "string" ? profile.preferred_language : null,
    emergency_contacts: normalizeEmergencyContacts(profile?.emergency_contacts),
    primary_doctor: normalizePrimaryDoctor(profile?.primary_doctor),
    preferred_hospitals: normalizePreferredHospitals(profile?.preferred_hospitals),
    health_insurance: normalizeHealthInsurance(profile?.health_insurance),
    care_reminders_enabled: typeof profile?.care_reminders_enabled === "boolean" ? profile.care_reminders_enabled : true,
    default_timezone: typeof profile?.default_timezone === "string" ? profile.default_timezone : null,
    created_at: typeof profile?.created_at === "string" ? profile.created_at : null,
    updated_at: typeof profile?.updated_at === "string" ? profile.updated_at : null,
  };
}

function textOrNull(value: unknown) {
  return typeof value === "string" && value.trim() ? value : null;
}

function normalizeAddress(value: unknown): ProfileAddress | null {
  if (!value || typeof value !== "object") {
    return null;
  }

  const record = value as Partial<ProfileAddress>;
  const address = {
    line1: textOrNull(record.line1),
    line2: textOrNull(record.line2),
    city: textOrNull(record.city),
    region: textOrNull(record.region),
    postal_code: textOrNull(record.postal_code),
    country_code: textOrNull(record.country_code)?.toUpperCase() ?? null,
  };

  return Object.values(address).some(Boolean) ? address : null;
}

function normalizeEmergencyContacts(value: unknown): EmergencyContact[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((contact) => {
      if (!contact || typeof contact !== "object") {
        return null;
      }

      const record = contact as Partial<EmergencyContact>;
      const name = textOrNull(record.name);
      const relationship = textOrNull(record.relationship);
      const primaryMobileNumber = textOrNull(record.primary_mobile_number);
      const priority = typeof record.priority === "number" && Number.isFinite(record.priority) ? record.priority : 999;

      if (!name || !relationship || !primaryMobileNumber) {
        return null;
      }

      return {
        name,
        relationship,
        primary_mobile_number: primaryMobileNumber,
        alternate_mobile_number: textOrNull(record.alternate_mobile_number),
        whatsapp_number: textOrNull(record.whatsapp_number),
        address: normalizeAddress(record.address),
        priority,
      };
    })
    .filter((contact): contact is EmergencyContact => Boolean(contact))
    .sort((left, right) => left.priority - right.priority)
    .slice(0, 3);
}

function normalizePrimaryDoctor(value: unknown): PrimaryDoctor | null {
  if (!value || typeof value !== "object") {
    return null;
  }

  const record = value as Partial<PrimaryDoctor>;
  const doctor = {
    name: textOrNull(record.name),
    speciality: textOrNull(record.speciality),
    hospital_clinic_name: textOrNull(record.hospital_clinic_name),
    mobile_number: textOrNull(record.mobile_number),
    emergency_contact_number: textOrNull(record.emergency_contact_number),
    address: normalizeAddress(record.address),
    email: textOrNull(record.email),
    preferred_for_emergency: record.preferred_for_emergency === true,
  };

  return Object.entries(doctor).some(([key, item]) => key !== "preferred_for_emergency" && Boolean(item)) || doctor.preferred_for_emergency
    ? doctor
    : null;
}

function normalizeHospital(value: unknown): PreferredHospital | null {
  if (!value || typeof value !== "object") {
    return null;
  }

  const record = value as Partial<PreferredHospital>;
  const name = textOrNull(record.name);
  const emergencyNumber = textOrNull(record.emergency_number);
  const address = normalizeAddress(record.address);
  if (!name || !emergencyNumber || !address) {
    return null;
  }

  return {
    name,
    emergency_number: emergencyNumber,
    address,
    ambulance_contact_number: textOrNull(record.ambulance_contact_number),
  };
}

function normalizePreferredHospitals(value: unknown): PreferredHospitals | null {
  if (!value || typeof value !== "object") {
    return null;
  }

  const record = value as Partial<PreferredHospitals>;
  const hospitals = {
    primary: normalizeHospital(record.primary),
    alternate: normalizeHospital(record.alternate),
  };

  return hospitals.primary || hospitals.alternate ? hospitals : null;
}

function normalizeHealthInsurance(value: unknown): HealthInsurance | null {
  if (!value || typeof value !== "object") {
    return null;
  }

  const record = value as Partial<HealthInsurance>;
  const insurance = {
    insurance_company: textOrNull(record.insurance_company),
    policy_name: textOrNull(record.policy_name),
    policy_number: textOrNull(record.policy_number),
    insurance_company_contact_email: textOrNull(record.insurance_company_contact_email),
    insurance_company_contact_phone: textOrNull(record.insurance_company_contact_phone),
  };

  return Object.values(insurance).some(Boolean) ? insurance : null;
}

async function requestProfile<TBody>(
  apiBaseUrl: string,
  accessToken: string | undefined,
  method: "GET" | "PATCH",
  body?: TBody,
  signal?: AbortSignal,
) {
  if (!accessToken) {
    throw new AccountServiceError("Please sign in again to manage your profile.");
  }

  const response = await fetch(`${apiBaseUrl}/v1/me/profile`, {
    method,
    signal,
    headers: {
      Accept: "application/json",
      Authorization: `Bearer ${accessToken}`,
      ...(body ? { "Content-Type": "application/json" } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  });

  if (!response.ok) {
    if (response.status === 403 && (await errorCode(response)) === "account_suspended") {
      throw new AccountSuspendedError();
    }

    if (response.status === 400) {
      throw new AccountServiceError("Please check your profile details and try again.");
    }

    if (response.status === 401 || response.status === 403) {
      throw new AccountServiceError("Please sign in again to manage your profile.");
    }

    throw new AccountServiceError();
  }

  const data = (await response.json()) as ProfileResponse;
  return normalizeProfile(data.profile);
}

function filenameFromContentDisposition(value: string | null) {
  const match = value?.match(/filename="?([^"]+)"?/i);
  return match?.[1] ?? "medopilot-health-profile.pdf";
}

async function requestHealthProfileExport(
  apiBaseUrl: string,
  accessToken: string | undefined,
  sections: HealthProfileExportSection[],
): Promise<HealthProfileExport> {
  if (!accessToken) {
    throw new AccountServiceError("Please sign in again to export your profile.");
  }

  const response = await fetch(`${apiBaseUrl}/v1/me/profile/export`, {
    method: "POST",
    headers: {
      Accept: "application/pdf",
      Authorization: `Bearer ${accessToken}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ sections }),
  });

  if (!response.ok) {
    if (response.status === 400) {
      throw new AccountServiceError("Choose at least one report section and try again.");
    }

    if (response.status === 401 || response.status === 403) {
      throw new AccountServiceError("Please sign in again to export your profile.");
    }

    throw new AccountServiceError("We could not generate your health profile PDF right now.");
  }

  return {
    blob: await response.blob(),
    filename: filenameFromContentDisposition(response.headers.get("content-disposition")),
  };
}

export function createAccountServiceClient(apiBaseUrl: string, getAccessToken: () => string | undefined) {
  return {
    getProfile(signal?: AbortSignal) {
      return requestProfile(apiBaseUrl, getAccessToken(), "GET", undefined, signal);
    },
    updateProfile(payload: AccountProfilePatch) {
      return requestProfile(apiBaseUrl, getAccessToken(), "PATCH", payload);
    },
    exportHealthProfile(sections: HealthProfileExportSection[]) {
      return requestHealthProfileExport(apiBaseUrl, getAccessToken(), sections);
    },
    getAccountLifecycle(signal?: AbortSignal) {
      return requestAccountLifecycle(apiBaseUrl, getAccessToken(), "GET", signal);
    },
    requestAccountDeletion() {
      return requestAccountLifecycle(apiBaseUrl, getAccessToken(), "POST");
    },
    cancelAccountDeletion() {
      return requestAccountLifecycle(apiBaseUrl, getAccessToken(), "DELETE");
    },
  };
}
